import React from "react";
import "./AdminNeonCard.css";

export default function AdminNeonCard({ contract, children }) {
    if (!contract) {
        return (
            <div className="admin-neon-wrapper">
                <div className="admin-neon-device loading">
                    <div className="admin-neon-screen">
                        <h2 className="admin-neon-title">LOADING CONTRACT...</h2>
                    </div>
                </div>
            </div>
        );
    }

    const {
        scoutName,
        clubName,
        agentName,
        playerName,
        playerUsername,
        contractDuration,
        timeLeft,
        signedAt,
        statusText,
        badge,
        // Optional hints (won't break if absent)
        completionType,
        status, // if your raw contract includes this
        uiContractTitle, // if frontend ever sets it later
    } = contract;

    const shouldGlowStatus =
        typeof statusText === "string" &&
        ["active", "admin approved"].includes(statusText.trim().toLowerCase());

    // ✅ Hide optional rows when empty/placeholder
    const hasAgent =
        typeof agentName === "string" && agentName.trim() && agentName.trim() !== "—";
    const hasClub =
        typeof clubName === "string" && clubName.trim() && clubName.trim() !== "—";

    const hasScout =
        typeof scoutName === "string" && scoutName.trim() && scoutName.trim() !== "—";
    const hasPlayer =
        typeof playerName === "string" && playerName.trim() && playerName.trim() !== "—";

    const signedAtText = signedAt ? new Date(signedAt).toLocaleString() : null;

    const playerLine =
        playerUsername && playerUsername !== "—"
            ? `${playerName} (${playerUsername})`
            : playerName;

    // ✅ Title logic (fixes "ACTIVE CONTRACT" showing everywhere)
    const computeTitle = () => {
        // 1) Strong override if provided
        if (typeof uiContractTitle === "string" && uiContractTitle.trim()) {
            return uiContractTitle.trim().toUpperCase();
        }

        // 2) Completed feed provides completionType: "terminated" | "expired"
        const ct = String(completionType || "").toLowerCase().trim();
        if (ct === "terminated") return "TERMINATED CONTRACT";
        if (ct === "expired") return "EXPIRED CONTRACT";

        // 3) Fallback: infer from statusText or status
        const st = String(statusText || status || "").toLowerCase().trim();
        if (st === "terminated") return "TERMINATED CONTRACT";
        if (st === "expired") return "EXPIRED CONTRACT";

        // Default: active/expiring
        return "ACTIVE CONTRACT";
    };

    const titleText = computeTitle();

    return (
        <div className="admin-neon-wrapper">
            <div className="admin-neon-device">
                {/* corner lights */}
                <span className="admin-corner tl" />
                <span className="admin-corner tr" />
                <span className="admin-corner bl" />
                <span className="admin-corner br" />

                <div className="admin-neon-screen">
                    {/* Title */}
                    <div className="admin-title-row">
                        <div className="admin-line" />
                        <h2 className="admin-neon-title">{titleText}</h2>
                        <div className="admin-line" />
                    </div>

                    {/* Contract details */}
                    <div className="admin-neon-content">
                        {hasScout ? <Row label="Scout" value={scoutName} /> : null}

                        {/* ✅ Only show if actually present */}
                        {hasAgent ? <Row label="Agent" value={agentName} /> : null}

                        {/* ✅ Only show if actually present */}
                        {hasClub ? <Row label="Club" value={clubName} /> : null}

                        {hasPlayer ? <Row label="Player" value={playerLine} /> : null}

                        {contractDuration ? <Row label="Duration" value={contractDuration} /> : null}

                        {timeLeft ? <Row label="Time Left" value={timeLeft} glow /> : null}

                        {signedAtText ? <Row label="Signed At" value={signedAtText} /> : null}

                        {statusText ? (
                            <Row label="Status" value={statusText} glow={shouldGlowStatus} />
                        ) : null}
                    </div>

                    {/* Extra area (buttons / revenue info / flags etc.) */}
                    {children ? <div className="admin-neon-extras">{children}</div> : null}

                    {/* Badge */}
                    {badge ? (
                        <div className="admin-status-badge">
                            <small>{badge.subtitle}</small>
                            <strong>{badge.title}</strong>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, glow }) {
    return (
        <div className="admin-row">
            <span className="admin-label">{label}:</span>
            <span className={glow ? "admin-value glow" : "admin-value"}>
                {value}
            </span>
        </div>
    );
}